import type { PagedResult, SoftDeleteAuditItem } from "../WarehouseApi";
import type { WarehouseApiModuleContext } from "./WarehouseApiModuleContext";

type ExtractApiError = (payload: unknown) => string | null;
type NormalizeSoftDeleteAuditItem = (value: unknown) => SoftDeleteAuditItem;
type ToSafeNonNegativeInt = (value: unknown, fallback: number) => number;

interface AuditApiModuleDependencies {
  extractApiError: ExtractApiError;
  normalizeSoftDeleteAuditItem: NormalizeSoftDeleteAuditItem;
  toSafeNonNegativeInt: ToSafeNonNegativeInt;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export class AuditApiModule {
  constructor(
    private readonly context: Pick<WarehouseApiModuleContext, "getJson">,
    private readonly deps: AuditApiModuleDependencies,
  ) {}

  async getSoftDeleteAudit(
    input: {
      entityType?: "product" | "box";
      entityId?: string;
      actorUserId?: string;
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<PagedResult<SoftDeleteAuditItem>> {
    const query = new URLSearchParams();

    if (input.entityType) {
      query.set("entityType", input.entityType);
    }
    if (input.entityId && input.entityId.trim()) {
      query.set("entityId", input.entityId.trim());
    }
    if (input.actorUserId && input.actorUserId.trim()) {
      query.set("actorUserId", input.actorUserId.trim());
    }
    if (typeof input.limit === "number" && Number.isFinite(input.limit)) {
      query.set("limit", String(Math.max(1, Math.floor(input.limit))));
    }
    if (typeof input.offset === "number" && Number.isFinite(input.offset)) {
      query.set("offset", String(Math.max(0, Math.floor(input.offset))));
    }

    const path = query.toString()
      ? `/api/audit/soft-delete?${query.toString()}`
      : "/api/audit/soft-delete";
    const payload = await this.context.getJson<unknown>(path);

    if (
      !isRecord(payload) ||
      payload.success !== true ||
      !Array.isArray(payload.data)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при загрузке аудита удалений",
      );
    }

    return {
      items: payload.data.map((item) =>
        this.deps.normalizeSoftDeleteAuditItem(item),
      ),
      total: this.deps.toSafeNonNegativeInt(payload.total, payload.data.length),
      offset: this.deps.toSafeNonNegativeInt(payload.offset, 0),
      limit:
        payload.limit === null || payload.limit === undefined
          ? null
          : this.deps.toSafeNonNegativeInt(payload.limit, 0),
    };
  }
}
